import { z } from 'zod';
import { fxFontsUsed } from './motion/fx-fonts';
import { docMotionSchema, layerMotionSchema } from './motion/schema';

/**
 * Template document — the single source of truth for a banner design.
 * The same JSON is rendered by the editor (browser) and by the render worker
 * (headless Chromium), so every visual property must live here.
 */

export const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_COLOR_RE =
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/;

const isPlainColor = (v: string) =>
  HEX_COLOR_RE.test(v) || RGB_COLOR_RE.test(v) || v === 'transparent';

export const colorSchema = z
  .string()
  .trim()
  .refine(
    // A brand kit colour: "brand:<slot>|<fallback colour>" (see brand.ts)
    (v) =>
      isPlainColor(v) || (/^brand:[a-z-]+\|/.test(v) && isPlainColor(v.slice(v.indexOf('|') + 1))),
    { message: 'Expected a color like #RRGGBB, #RRGGBBAA, rgb() or rgba()' },
  );
export type Color = z.infer<typeof colorSchema>;

export const gradientStopSchema = z.object({
  offset: z.number().min(0).max(1),
  color: colorSchema,
});

export const solidFillSchema = z.object({
  type: z.literal('solid'),
  color: colorSchema,
});

export const linearFillSchema = z.object({
  type: z.literal('linear'),
  angle: z.number().min(-360).max(360),
  stops: z.array(gradientStopSchema).min(2).max(10),
});

export const radialFillSchema = z.object({
  type: z.literal('radial'),
  stops: z.array(gradientStopSchema).min(2).max(10),
});

export const fillSchema = z.discriminatedUnion('type', [
  solidFillSchema,
  linearFillSchema,
  radialFillSchema,
]);
export type Fill = z.infer<typeof fillSchema>;

export const shadowSchema = z.object({
  x: z.number().min(-500).max(500),
  y: z.number().min(-500).max(500),
  blur: z.number().min(0).max(500),
  color: colorSchema,
});
export type Shadow = z.infer<typeof shadowSchema>;

export const paddingSchema = z.object({
  top: z.number().min(0).max(2000),
  right: z.number().min(0).max(2000),
  bottom: z.number().min(0).max(2000),
  left: z.number().min(0).max(2000),
});
export type Padding = z.infer<typeof paddingSchema>;

/** Layer names are used as keys in the API, CSV columns and signed URLs. */
export const LAYER_NAME_RE = /^[^.=&?#/\\\s][^.=&?#/\\]{0,63}$/u;
export const layerNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(
    LAYER_NAME_RE,
    'Layer name must not contain . = & ? # / \\ and must not start with a space',
  );

/**
 * How a layer follows the canvas when the template is resized ("smart resize").
 * `auto` keeps the ratio of the free space before and after the layer and stretches
 * full-width/full-height layers; `scale` is plain proportional scaling per axis.
 */
export const H_CONSTRAINTS = ['auto', 'left', 'center', 'right', 'stretch', 'scale'] as const;
export const V_CONSTRAINTS = ['auto', 'top', 'center', 'bottom', 'stretch', 'scale'] as const;
export type HConstraint = (typeof H_CONSTRAINTS)[number];
export type VConstraint = (typeof V_CONSTRAINTS)[number];
export const constraintsSchema = z.object({
  horizontal: z.enum(H_CONSTRAINTS).default('auto'),
  vertical: z.enum(V_CONSTRAINTS).default('auto'),
});
export type LayerConstraints = z.infer<typeof constraintsSchema>;

const baseLayerShape = {
  id: z.string().min(1).max(64),
  name: layerNameSchema,
  x: z.number().min(-100000).max(100000),
  y: z.number().min(-100000).max(100000),
  width: z.number().min(1).max(100000),
  height: z.number().min(1).max(100000),
  rotation: z.number().min(-360).max(360).default(0),
  opacity: z.number().min(0).max(1).default(1),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  /** Free-form note shown to API users ("Product title, max 40 chars"). */
  description: z.string().max(500).optional(),
  constraints: constraintsSchema.optional(),
  /** Id of the stack (group layer) this layer is laid out in. */
  parentId: z.string().min(1).max(64).optional(),
  /** Inside a stack: take the free space along the stack direction. */
  grow: z.boolean().optional(),
  /** Animation (videos and animated banners). Static renders ignore it. */
  motion: layerMotionSchema.optional(),
};

export const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'] as const;
export const VERTICAL_ALIGNS = ['top', 'middle', 'bottom'] as const;
export const TEXT_TRANSFORMS = ['none', 'uppercase', 'lowercase', 'capitalize'] as const;
export const TEXT_DECORATIONS = ['none', 'underline', 'line-through'] as const;
export const FONT_STYLES = ['normal', 'italic'] as const;
export const IMAGE_FITS = ['cover', 'contain', 'fill'] as const;
export const SHAPES = [
  'rectangle',
  'ellipse',
  'line',
  'triangle',
  'polygon',
  'star',
  'heart',
  'arrow',
  'chevron',
  'cross',
  'bubble',
  'ribbon',
  'tag',
  'parallelogram',
  'semicircle',
  'blob',
  'icon',
] as const;
export const STROKE_STYLES = ['solid', 'dashed', 'dotted'] as const;

/** A marker-style background behind each line of text (not the whole box). */
export const textHighlightSchema = z.object({
  color: colorSchema,
  paddingX: z.number().min(0).max(500).default(0),
  paddingY: z.number().min(0).max(500).default(0),
  radius: z.number().min(0).max(500).default(0),
});
export type TextHighlight = z.infer<typeof textHighlightSchema>;

/** How an accented part of a text looks: only what differs from the rest of the text. */
export const textAccentSchema = z.object({
  color: colorSchema.optional(),
  fontWeight: z.number().int().min(100).max(1000).optional(),
  fontStyle: z.enum(FONT_STYLES).optional(),
  textDecoration: z.enum(TEXT_DECORATIONS).optional(),
  /** A marker behind the words. */
  background: colorSchema.optional(),
  /** Size against the rest of the text (1.5 = one and a half times bigger). */
  scale: z.number().min(0.2).max(5).optional(),
});
export type TextAccent = z.infer<typeof textAccentSchema>;

/** A fragment of the text styled apart (a word picked in the editor), wherever it appears. */
export const textMarkSchema = textAccentSchema.extend({
  text: z.string().min(1).max(500),
});
export type TextMark = z.infer<typeof textMarkSchema>;

export const textLayerSchema = z.object({
  ...baseLayerShape,
  type: z.literal('text'),
  text: z.string().max(20000),
  fontFamily: z.string().min(1).max(128),
  fontWeight: z.number().int().min(100).max(1000),
  fontStyle: z.enum(FONT_STYLES).default('normal'),
  fontSize: z.number().min(1).max(4000),
  color: colorSchema,
  lineHeight: z.number().min(0.3).max(10),
  letterSpacing: z.number().min(-200).max(2000),
  align: z.enum(TEXT_ALIGNS),
  verticalAlign: z.enum(VERTICAL_ALIGNS),
  textTransform: z.enum(TEXT_TRANSFORMS).default('none'),
  textDecoration: z.enum(TEXT_DECORATIONS).default('none'),
  /** Shrink the font size until the text fits the box. */
  autoFit: z.boolean().default(false),
  minFontSize: z.number().min(1).max(4000).default(8),
  /** 0 = unlimited. Extra lines are truncated with an ellipsis. */
  maxLines: z.number().int().min(0).max(1000).default(0),
  padding: paddingSchema.default({ top: 0, right: 0, bottom: 0, left: 0 }),
  background: fillSchema.nullable().default(null),
  highlight: textHighlightSchema.nullable().optional(),
  /**
   * Words picked in the editor and how each one looks. They are found in whatever text the
   * layer gets (a table row, the API), so a word the new text doesn't contain is just not styled.
   */
  marks: z.array(textMarkSchema).max(50).optional(),
  /**
   * The accent style of the layer: for `**words**` in the text (from a table or the API), for
   * the `accent` value of a row, and for numbers when `accentNumbers` is on.
   */
  accent: textAccentSchema.nullable().optional(),
  /** Numbers, prices and percentages ("−15%", "18 500+", "1 299 грн") in the accent style. */
  accentNumbers: z.boolean().optional(),
  borderRadius: z.number().min(0).max(10000).default(0),
  strokeWidth: z.number().min(0).max(200).default(0),
  strokeColor: colorSchema.default('#000000'),
  shadow: shadowSchema.nullable().default(null),
  /**
   * The font the design asked for when it was not available (an import from Figma): the text is
   * drawn with `fontFamily` instead, and the editor highlights it until the font is uploaded.
   */
  missingFont: z.string().max(128).optional(),
});
export type TextLayer = z.infer<typeof textLayerSchema>;

export const imageLayerSchema = z.object({
  ...baseLayerShape,
  type: z.literal('image'),
  /** http(s) URL, or a path to an uploaded file (/files/...). Empty = placeholder. */
  src: z.string().max(4096),
  fit: z.enum(IMAGE_FITS).default('cover'),
  /** Focal point for cover/contain (0..1). */
  focusX: z.number().min(0).max(1).default(0.5),
  focusY: z.number().min(0).max(1).default(0.5),
  borderRadius: z.number().min(0).max(10000).default(0),
  borderWidth: z.number().min(0).max(1000).default(0),
  borderColor: colorSchema.default('#000000'),
  grayscale: z.boolean().default(false),
  blur: z.number().min(0).max(200).default(0),
  brightness: z.number().min(0).max(3).default(1),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
  shadow: shadowSchema.nullable().default(null),
  /**
   * A video clip in place of the picture; `src` stays its poster frame, shown by still renders
   * and the design editor. The clip plays in animations and video renders.
   */
  video: z
    .object({
      src: z.string().max(4096),
      /** Length of the clip, ms. */
      duration: z.number().min(0).max(3_600_000),
      /** Where in the clip playback starts, ms. */
      trimStart: z.number().min(0).max(3_600_000).default(0),
      /** Where it ends, ms (none: the end of the file). */
      trimEnd: z.number().min(0).max(3_600_000).nullable().optional(),
      /** Playback speed: 0.5 = slow motion, 2 = twice as fast. */
      speed: z.number().min(0.25).max(4).default(1),
      loop: z.boolean().default(true),
      muted: z.boolean().default(true),
      volume: z.number().min(0).max(1).default(1),
    })
    .nullable()
    .optional(),
});
export type ImageLayer = z.infer<typeof imageLayerSchema>;

type ClipTiming = Pick<NonNullable<ImageLayer['video']>, 'duration' | 'trimStart' | 'loop'> &
  Partial<Pick<NonNullable<ImageLayer['video']>, 'trimEnd' | 'speed'>>;

/** End of the used part of a clip, ms in the file. */
export function clipEnd(video: ClipTiming): number {
  const end = video.trimEnd ?? video.duration;
  return Math.max(video.trimStart + 1, Math.min(video.duration || end, end));
}

/** How long one pass of the used part plays, ms of video time (speed applied). */
export function clipPlayLength(video: ClipTiming): number {
  return (clipEnd(video) - video.trimStart) / (video.speed || 1);
}

/**
 * Clip time (ms in the file) shown `ms` after the clip starts playing (the start of its scene,
 * or of the video): trimmed, sped up or slowed down, looped or held on its last frame.
 */
export function clipTime(video: ClipTiming, ms: number): number {
  const length = Math.max(1, clipEnd(video) - video.trimStart);
  const t = Math.max(0, ms) * (video.speed || 1);
  return video.trimStart + (video.loop ? t % length : Math.min(t, length - 1));
}

export const shapeLayerSchema = z.object({
  ...baseLayerShape,
  type: z.literal('shape'),
  shape: z.enum(SHAPES),
  fill: fillSchema.nullable(),
  strokeWidth: z.number().min(0).max(1000).default(0),
  strokeColor: colorSchema.default('#000000'),
  strokeStyle: z.enum(STROKE_STYLES).default('solid'),
  borderRadius: z.number().min(0).max(10000).default(0),
  shadow: shadowSchema.nullable().default(null),
  /** Polygon sides or star rays. */
  points: z.number().int().min(3).max(64).optional(),
  /** Star: inner radius relative to the outer one (small = spiky, close to 1 = a seal). */
  innerRatio: z.number().min(0.05).max(1).optional(),
  /** `icon` shapes: id from the built-in icon set, drawn in the fill colour. */
  icon: z.string().max(40).optional(),
});
export type ShapeLayer = z.infer<typeof shapeLayerSchema>;

export const QR_DOT_STYLES = ['square', 'rounded', 'dots'] as const;
export const QR_EYE_STYLES = ['square', 'rounded', 'circle'] as const;

export const qrLayerSchema = z.object({
  ...baseLayerShape,
  type: z.literal('qr'),
  value: z.string().max(2000),
  foreground: colorSchema,
  background: colorSchema.nullable(),
  margin: z.number().int().min(0).max(20).default(1),
  errorCorrection: z.enum(['L', 'M', 'Q', 'H']).default('M'),
  // Styling (Pro and Business; other plans render the classic square code, see plainQr)
  dotStyle: z.enum(QR_DOT_STYLES).default('square'),
  eyeStyle: z.enum(QR_EYE_STYLES).default('square'),
  /** Colour of the three corner squares; null = foreground. */
  eyeColor: colorSchema.nullable().default(null),
  /** Image in the centre (URL or /files/ path); the code then uses error correction H. */
  logo: z.string().max(4096).default(''),
  /** Logo width as a share of the code (without the quiet zone). */
  logoSize: z.number().min(0.1).max(0.3).default(0.22),
});
export type QrLayer = z.infer<typeof qrLayerSchema>;

/** Same code without the paid styling: square modules and eyes, no logo. */
export function plainQr<T extends QrLayer>(layer: T): T {
  return { ...layer, dotStyle: 'square', eyeStyle: 'square', eyeColor: null, logo: '' };
}

export const hasQrStyling = (l: QrLayer) =>
  (l.dotStyle ?? 'square') !== 'square' ||
  (l.eyeStyle ?? 'square') !== 'square' ||
  Boolean(l.eyeColor) ||
  Boolean(l.logo);

/**
 * `free`: no auto layout. The layers keep the place they were drawn at, measured from the stack's
 * top-left corner (a text on a shape for a hand-made button), and move with the stack.
 */
export const STACK_DIRECTIONS = ['vertical', 'horizontal', 'auto', 'free'] as const;
export const STACK_ALIGNS = ['start', 'center', 'end', 'stretch'] as const;
export const STACK_JUSTIFY = ['start', 'center', 'end', 'space-between'] as const;

/**
 * Stack (auto layout): its children (layers with `parentId` = this id, in document order) are
 * laid out in a column or a row with a gap, like CSS flexbox. Text and nested stacks take the
 * size their content needs, so longer text pushes the next items along. `auto` is a row when the
 * canvas is at least `autoRowRatio` times wider than tall and a column otherwise, so one design
 * works in landscape and portrait sizes.
 */
export const groupLayerSchema = z.object({
  ...baseLayerShape,
  type: z.literal('group'),
  direction: z.enum(STACK_DIRECTIONS).default('vertical'),
  autoRowRatio: z.number().min(0.1).max(20).default(1),
  gap: z.number().min(0).max(10000).default(0),
  padding: paddingSchema.default({ top: 0, right: 0, bottom: 0, left: 0 }),
  alignItems: z.enum(STACK_ALIGNS).default('start'),
  justify: z.enum(STACK_JUSTIFY).default('start'),
  fill: fillSchema.nullable().default(null),
  borderRadius: z.number().min(0).max(10000).default(0),
  /** Hide whatever does not fit instead of letting it spill out. */
  clip: z.boolean().default(false),
});
export type GroupLayer = z.infer<typeof groupLayerSchema>;

export const layerSchema = z.discriminatedUnion('type', [
  textLayerSchema,
  imageLayerSchema,
  shapeLayerSchema,
  qrLayerSchema,
  groupLayerSchema,
]);
export type Layer = z.infer<typeof layerSchema>;
export type LayerType = Layer['type'];
export const LAYER_TYPES: LayerType[] = ['text', 'image', 'shape', 'qr', 'group'];
export const MAX_STACK_DEPTH = 6;

export const MIN_TEMPLATE_SIZE = 16;
export const MAX_TEMPLATE_SIZE = 8000;
/** Largest output side in pixels (template size × scale): bigger screenshots exhaust Chromium. */
export const MAX_RENDER_SIDE = 16000;

export const templateDocSchema = z
  .object({
    width: z.number().int().min(MIN_TEMPLATE_SIZE).max(MAX_TEMPLATE_SIZE),
    height: z.number().int().min(MIN_TEMPLATE_SIZE).max(MAX_TEMPLATE_SIZE),
    background: fillSchema.nullable(),
    layers: z.array(layerSchema).max(500),
    /** Present on animated templates: timing, scenes and the motion style. */
    motion: docMotionSchema.optional(),
    /** Brand used when a render doesn't choose one (see brand.ts). */
    brandId: z.string().max(64).nullable().optional(),
  })
  .superRefine((doc, ctx) => {
    const ids = new Set<string>();
    const names = new Set<string>();
    doc.layers.forEach((layer, index) => {
      if (ids.has(layer.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['layers', index, 'id'],
          message: `Duplicate layer id "${layer.id}"`,
        });
      }
      ids.add(layer.id);
      if (names.has(layer.name)) {
        ctx.addIssue({
          code: 'custom',
          path: ['layers', index, 'name'],
          message: `Duplicate layer name "${layer.name}" — layer names must be unique`,
        });
      }
      names.add(layer.name);
    });
    // Stack membership: the parent must be a stack, and parents must not form a cycle
    const byId = new Map(doc.layers.map((l) => [l.id, l]));
    doc.layers.forEach((layer, index) => {
      if (!layer.parentId) return;
      const parent = byId.get(layer.parentId);
      if (!parent || parent.type !== 'group' || parent.id === layer.id) {
        ctx.addIssue({
          code: 'custom',
          path: ['layers', index, 'parentId'],
          message: `Layer "${layer.name}" refers to a missing stack "${layer.parentId}"`,
        });
        return;
      }
      let depth = 0;
      for (
        let p: Layer | undefined = parent;
        p;
        p = p.parentId ? byId.get(p.parentId) : undefined
      ) {
        if (++depth > MAX_STACK_DEPTH || p.id === layer.id) {
          ctx.addIssue({
            code: 'custom',
            path: ['layers', index, 'parentId'],
            message: `Stacks around "${layer.name}" are nested too deep or form a cycle`,
          });
          return;
        }
      }
    });
  });
export type TemplateDoc = z.infer<typeof templateDocSchema>;

/** Parse untrusted input into a fully defaulted template document. */
export function parseTemplateDoc(input: unknown): TemplateDoc {
  return templateDocSchema.parse(input);
}

export function safeParseTemplateDoc(input: unknown) {
  return templateDocSchema.safeParse(input);
}

export function getLayerByName(doc: TemplateDoc, name: string): Layer | undefined {
  return doc.layers.find((l) => l.name === name);
}

export function fontFamiliesUsed(
  doc: TemplateDoc,
): Array<{ family: string; weight: number; style: string }> {
  const seen = new Map<string, { family: string; weight: number; style: string }>();
  for (const layer of doc.layers) {
    if (layer.type !== 'text') continue;
    const key = `${layer.fontFamily}|${layer.fontWeight}|${layer.fontStyle}`;
    if (!seen.has(key)) {
      seen.set(key, { family: layer.fontFamily, weight: layer.fontWeight, style: layer.fontStyle });
    }
  }
  // Motion graphics that bring their own font (terminal, code, HUD)
  for (const { family } of fxFontsUsed(doc))
    if (![...seen.values()].some((f) => f.family === family))
      seen.set(`${family}|500|normal`, { family, weight: 500, style: 'normal' });
  return [...seen.values()];
}
